<script setup lang="ts">
import { type AuctionItemEntity, type AuctionListingItemEntity, AuctionState } from '~/apiClient';

// PROPS
interface Props {
    auction: AuctionItemEntity | AuctionListingItemEntity;
}

const { auction } = defineProps<Props>();

// COMPOSABLES
const { tt } = useTypedI18n();

// DATA
const maxBidderData = computed(() => {
    if (auction.state !== AuctionState.Running) return;

    if (auction.userMeta) {
        const { outBid, maxBidder, colleagueMaxBidder } = auction.userMeta;

        return outBid || maxBidder ? { isMaxBidder: maxBidder, isColleagueMaxBidder: colleagueMaxBidder } : undefined;
    }

    if (auction.bidInfo) {
        return { isMaxBidder: auction.bidInfo.maxBidder, isColleagueMaxBidder: auction.bidInfo.colleagueMaxBidder };
    }
});

// The group leads with a colleague's bid, a sibling company's included: it is not the viewer's own (BSD-4556).
const tooltipText = computed(() => {
    if (!maxBidderData.value?.isMaxBidder) return tt('sdp.Auction.auctionStatus.outBid');

    return tt(maxBidderData.value.isColleagueMaxBidder ? 'sdp.Auction.auctionStatus.colleagueMaxBidder' : 'sdp.Auction.auctionStatus.maxBidder');
});
</script>

<template>
    <BaseTooltip v-if="maxBidderData" :text="tooltipText">
        <span class="flex h-8 w-8 items-center justify-center rounded-full" :class="maxBidderData.isMaxBidder ? 'bg-success-50' : 'bg-amber-50'">
            <UIcon v-if="maxBidderData.isMaxBidder" name="lucide:crown" class="text-success-600 size-5" />
            <BaseOffIcon v-else name="lucide:crown" color-class="text-warning-600" slash-outline-class="outline-amber-50" class="size-5" />
        </span>
    </BaseTooltip>
</template>
